/**
 * Initials entry overlay (sc-3014 v1).
 *
 * Classic arcade UX: 3 letter slots. Up/Down cycles the focused slot's
 * letter A→Z (and wraps to space — "_ _ _" if the player wants less than
 * 3 letters, then "JA_" etc.). Left/Right moves between slots. Fire
 * (Z/Enter/space) confirms.
 *
 * Style matches pause-overlay.ts: same scrim, panel border, palette,
 * blink cadence. Lives in the cartridge canvas (480x270) so we render
 * with the same dimensions.
 *
 * Returns a Promise<string> resolving to 1-3 uppercase letters once
 * confirmed. The trailing slots are stripped if they're spaces.
 */

import type { InputSource } from './loop.js';

// WIDTH / HEIGHT are read live from g.canvas at each render call so the
// overlay positions correctly under whichever cartridge resolution is
// active (480x270 vs 960x540 vector).

const SCRIM = 'rgba(4, 6, 13, 0.55)';
const PANEL_BG = 'rgba(10, 4, 24, 0.78)';
const PANEL_BORDER = '#ffea00';
const TITLE = '#ffea00';
const LABEL = '#7af3c8';
const SLOT_FOCUSED = '#ffea00';
const SLOT_DIM = 'rgba(255, 234, 0, 0.4)';
const HINT = '#7af3c8';
const SCORE_COLOR = '#00d9ff';

/** A-Z plus an explicit blank ("space") so the player can submit
 *  initials shorter than 3 letters. The blank is rendered as "_". */
const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ '.split('');

export interface InitialsEntryOpts {
  /** Final score to display at the top — gives context for why the
   *  player is entering initials. */
  score: number;
  /** Rank the player just earned, if known (e.g. "RANK #3"). Optional
   *  because we don't always have it at entry time (score submitted
   *  AFTER initials are picked in v1). */
  rank?: number;
  /** Defaulted initials — usually the player's last-used entries. */
  defaultInitials?: string;
  /** Optional title; defaults to "NEW HIGH SCORE". Lets future calls
   *  (e.g. daily challenge) override. */
  title?: string;
}

export function runInitialsEntry(
  ctx2d: CanvasRenderingContext2D,
  input: InputSource,
  opts: InitialsEntryOpts
): Promise<string> {
  // Slot state: indices into CHARSET. Default to last-used, padded with
  // blanks to 3 slots.
  const initial = (opts.defaultInitials ?? '').toUpperCase().padEnd(3, ' ');
  const slots: number[] = [0, 1, 2].map((i) => {
    const ch = initial.charAt(i);
    const idx = CHARSET.indexOf(ch);
    return idx >= 0 ? idx : 0;
  });
  let focus = 0;
  let blink = 0;
  let repeatTick = 0; // Auto-repeat for held up/down (so cycling A→Z is fast).
  const REPEAT_INITIAL = 18;
  const REPEAT_RATE = 4;

  return new Promise<string>((resolve) => {
    const tick = (): void => {
      const state = input.snapshot();

      // Horizontal slot navigation — no repeat, single-press only.
      if (state.justPressed.has('left') && focus > 0) {
        focus -= 1;
      } else if (state.justPressed.has('right') && focus < 2) {
        focus += 1;
      }

      // Vertical letter cycling with auto-repeat. justPressed gives the
      // edge; the boolean fields (state.up / state.down) give the held
      // state for the auto-repeat ramp.
      const upEdge = state.justPressed.has('up');
      const downEdge = state.justPressed.has('down');
      if (upEdge || downEdge) {
        cycleSlot(slots, focus, upEdge ? -1 : 1);
        repeatTick = -REPEAT_INITIAL;
      } else if (state.up || state.down) {
        repeatTick += 1;
        if (repeatTick >= 0 && repeatTick % REPEAT_RATE === 0) {
          cycleSlot(slots, focus, state.up ? -1 : 1);
        }
      } else {
        repeatTick = 0;
      }

      // Confirm: fire / start / coin all work. The cabinet has
      // historically been generous about which "confirm" key works on
      // any given menu, so match that.
      if (
        state.justPressed.has('fireA') ||
        state.justPressed.has('start') ||
        state.justPressed.has('coin')
      ) {
        const built = slots.map((i) => CHARSET[i] ?? ' ').join('').trimEnd();
        // Empty (all blanks) → resolve to "AAA" as a fallback so the
        // server-side regex /^[A-Z]{1,3}$/ stays happy. Players who
        // genuinely don't want initials can just leave the defaults.
        resolve(built.length > 0 ? built : 'AAA');
        return;
      }

      // Repaint — caller pre-renders the cartridge final frame before
      // calling us, but we re-scrim every frame so blink animates.
      render(ctx2d, opts, slots, focus, blink);
      blink = (blink + 1) % 60;
      requestAnimationFrame(tick);
    };

    tick();
  });
}

function cycleSlot(slots: number[], idx: number, dir: -1 | 1): void {
  const current = slots[idx] ?? 0;
  const next = (current + dir + CHARSET.length) % CHARSET.length;
  slots[idx] = next;
}

function render(
  g: CanvasRenderingContext2D,
  opts: InitialsEntryOpts,
  slots: number[],
  focus: number,
  blink: number
): void {
  const WIDTH = g.canvas.width;
  const HEIGHT = g.canvas.height;
  g.fillStyle = SCRIM;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  const panelW = 280;
  const panelH = 150;
  const panelX = WIDTH / 2 - panelW / 2;
  const panelY = HEIGHT / 2 - panelH / 2;

  g.fillStyle = PANEL_BG;
  g.fillRect(panelX, panelY, panelW, panelH);
  g.strokeStyle = PANEL_BORDER;
  g.lineWidth = 2;
  g.strokeRect(panelX + 0.5, panelY + 0.5, panelW - 1, panelH - 1);

  g.textAlign = 'center';
  g.textBaseline = 'top';

  g.fillStyle = TITLE;
  g.font = 'bold 14px ui-monospace, monospace';
  g.fillText(opts.title ?? 'NEW HIGH SCORE', WIDTH / 2, panelY + 12);

  g.font = '10px ui-monospace, monospace';
  g.fillStyle = SCORE_COLOR;
  const scoreLabel = formatScore(opts.score);
  if (opts.rank !== undefined) {
    g.fillText(`${scoreLabel}  ·  RANK #${opts.rank}`, WIDTH / 2, panelY + 32);
  } else {
    g.fillText(scoreLabel, WIDTH / 2, panelY + 32);
  }

  g.fillStyle = LABEL;
  g.font = '9px ui-monospace, monospace';
  g.fillText('ENTER YOUR INITIALS', WIDTH / 2, panelY + 54);

  // Three big letter slots.
  g.font = 'bold 36px ui-monospace, monospace';
  const slotSpacing = 56;
  const slotsCenterY = panelY + 92;
  for (let i = 0; i < 3; i++) {
    const ch = CHARSET[slots[i] ?? 0] ?? ' ';
    const display = ch === ' ' ? '_' : ch;
    const x = WIDTH / 2 + (i - 1) * slotSpacing;
    const focused = i === focus;
    g.fillStyle = focused ? SLOT_FOCUSED : SLOT_DIM;
    // Subtle underline marker beneath each slot.
    g.fillRect(x - 18, slotsCenterY + 22, 36, 2);
    // Letter, blinks on focused slot.
    if (focused && blink >= 40) {
      // Hide the letter half the time for the "cursor" feel.
      g.fillStyle = 'transparent';
    } else {
      g.fillStyle = focused ? SLOT_FOCUSED : SLOT_DIM;
    }
    if (g.fillStyle !== 'transparent') {
      g.fillText(display, x, slotsCenterY - 14);
    }
  }

  g.font = '8px ui-monospace, monospace';
  g.fillStyle = HINT;
  g.fillText(
    'UP/DN — LETTER    LF/RT — SLOT    Z — CONFIRM',
    WIDTH / 2,
    panelY + panelH + 8
  );

  g.textAlign = 'left';
  g.textBaseline = 'top';
}

function formatScore(score: number): string {
  return score.toLocaleString('en-US');
}
